import { useCallback, useEffect, useRef, useState } from 'react';

const STICK_PX = 80;

export function useStickToBottom(
  containerRef: React.RefObject<HTMLDivElement | null>,
  ticketId: string,
  items: readonly { authorId: string }[],
  currentUserId = '',
) {
  const stickRef = useRef(true);
  const countRef = useRef<number | null>(null);
  const ancestorRef = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  const [fresh, setFresh] = useState(0);

  const scrollToBottom = useCallback(
    (allowAncestor: boolean, smooth = false) => {
      const el = containerRef.current;
      if (!el) return;
      if (el.scrollHeight - el.clientHeight > 1) {
        if (smooth) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        else el.scrollTop = el.scrollHeight;
        return;
      }
      if (!allowAncestor) return;
      const last = el.lastElementChild;
      if (!last || last.getBoundingClientRect().top > window.innerHeight + 240) return;
      last.scrollIntoView({ block: smooth ? 'end' : 'nearest', behavior: smooth ? 'smooth' : 'auto' });
    },
    [containerRef],
  );

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
    stickRef.current = near;
    setAtBottom(near);
    if (near) setFresh(0);
  }, [containerRef]);

  useEffect(() => {
    stickRef.current = true;
    countRef.current = null;
    ancestorRef.current = false;
    setAtBottom(true);
    setFresh(0);
    const el = containerRef.current;
    if (el && el.scrollHeight - el.clientHeight > 1) el.scrollTop = el.scrollHeight;
  }, [ticketId, containerRef]);

  useEffect(() => {
    const count = items.length;
    if (countRef.current === null) {
      if (count > 0) countRef.current = count;
    } else {
      const added = count - countRef.current;
      if (added > 0) {
        ancestorRef.current = true;
        const last = items[count - 1];
        if (last && last.authorId === currentUserId) stickRef.current = true;
        else if (!stickRef.current) {
          const foreign = items.slice(-added).filter((m) => m.authorId !== currentUserId).length;
          if (foreign > 0) setFresh((n) => n + foreign);
        }
      }
      countRef.current = count;
    }
    if (stickRef.current) scrollToBottom(ancestorRef.current);
  }, [items, scrollToBottom, currentUserId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottom(ancestorRef.current);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, scrollToBottom]);

  const onImageLoad = useCallback(() => {
    if (stickRef.current) scrollToBottom(ancestorRef.current);
  }, [scrollToBottom]);

  const jumpToBottom = useCallback(() => {
    stickRef.current = true;
    setFresh(0);
    setAtBottom(true);
    scrollToBottom(true, true);
  }, [scrollToBottom]);

  return { handleScroll, onImageLoad, atBottom, fresh, jumpToBottom };
}
